import { Button, message, Spin, Table } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, MinusCircleOutlined } from '@ant-design/icons';

import {
  hasRelatedFlows,
  hasRelatedMetrics,
  isDeployed,
  isTraceId,
  isUndeployed,
  refetchDimensionRelated,
  refetchFlowRelated,
  refetchMappedEventRelated,
  refetchMetricRelated,
} from '@src/emp-3.0/utils';
import { EStatus } from '@src/emp-3.0/types';
import { useDeployListByIds } from '@api/emp/queryHooks';
import DeploymentTable from '@src/emp-3.0/components/DeploymentDrawer/DeploymentTable';
import { StyledDrawer, StyledHeader } from '@src/emp-3.0/components/DeploymentDrawer/styled';
import {
  generateCommonColumn,
  generateDateColumn,
  generateNameColumn,
  generateStatusColumn,
  generateTypeColumn,
  generateUserColumn,
} from '@src/emp-3.0/components/DeploymentDrawer/generateColumn';
import { useDeleteVersion } from '@components/metrics-builder/api/queryHooks';
import { useDeploy, useUndeploy } from '@src/emp-3.0/api/queryHooks';
import RequestCompletionPrompt from '@src/emp-3.0/components/Management/components/BulkAction/RequestCompletionPrompt';
import { DELETED, DEPLOYED, Id_Key_MAPPING, UNDEPLOYED } from '@src/emp-3.0/constants/management';
import {
  getIdsByResource,
  getNameKeyByResource,
  getPluralSuffix,
  isDeleteAction,
  isDeployAction,
  isUndeployAction,
} from '@src/emp-3.0/utils/management';

import { IBulkActionProps } from './typed';
import { BulkActionWrapper, Line, SeparatorWrapper, TableTitleWrapper, Footer } from './styled';
import { trackClickBulkActionManagement } from '@src/sensor';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

export const Separator = () => {
  return (
    <SeparatorWrapper>
      <Line />
    </SeparatorWrapper>
  );
};

const BulkAction: React.FC<IBulkActionProps> = ({
  resource,
  loading: propsLoading,
  data: propsData = [],
  onClose,
  show,
}) => {
  const { mutate: getDeployListByIds, isLoading: isLoadingDeployListByIds } = useDeployListByIds();
  const { mutate: deploy, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeploy, isLoading: isLoadingUndeploy } = useUndeploy();
  const { workbenchMode } = useInitialInfoStore();
  // const { mutate: bulkDelete, isLoading: isLoadingDelete } = useDelete();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<string | null>(null);
  const [undeployedStatusTableData, setUndeployedStatusTableData] = useState<any[]>([]);
  // Request completion
  const [requestStatus, setRequestStatus] = useState<'success' | 'failure' | null>(null);
  const [requestData, setRequestData] = useState<any[]>([]);
  const { mutate: deleteVersions, isLoading: isLoadingDelete } = useDeleteVersion();
  const loading = useMemo(
    () =>
      propsLoading ||
      isLoadingDeployListByIds ||
      isLoadingDeploy ||
      isLoadingUndeploy ||
      isLoadingDelete,
    [isLoadingDeploy, isLoadingDeployListByIds, isLoadingUndeploy, isLoadingDelete, propsLoading],
  );

  const isDeletedAction = useMemo(() => isDeleteAction(bulkActionType), [bulkActionType]);
  const isDeployedAction = useMemo(() => isDeployAction(bulkActionType), [bulkActionType]);
  const isUndeployedAction = useMemo(() => isUndeployAction(bulkActionType), [bulkActionType]);

  // 'flow_id', 'metric_id', 'event_id', 'custom_dimension_id'
  const idKey = Id_Key_MAPPING[resource];

  const categorizedPropsData = useMemo(() => {
    const createInhibitedItem = (item: any, reason: string[]) => ({
      ...item,
      action_inhibitors: reason,
    });

    const filterAndMap = (
      filterCondition: (item: any) => boolean,
      mapCondition?: (item: any) => any,
    ) => propsData.filter(filterCondition).map(mapCondition || ((item) => item));

    const isHasRelated = (item: any) => hasRelatedMetrics(item) || hasRelatedFlows(item);

    return {
      // Enabled bulk action data
      enabledDeployedActionData: propsData.filter(isUndeployed),

      enabledUndeployedActionData: propsData.filter(
        (item) => isDeployed(item) && !isTraceId(item) && !isHasRelated(item),
      ),

      enabledDeletedActionData: propsData.filter((item) => !isTraceId(item) && !isHasRelated(item)),

      // Disabled bulk action data
      disabledDeployedActionData: filterAndMap(isDeployed, (item) =>
        createInhibitedItem(item, ['Already in deployed status.']),
      ),

      disabledUndeployActionData: filterAndMap(
        (item) => isUndeployed(item) || isTraceId(item) || isHasRelated(item),
        (item) => {
          if (isTraceId(item)) {
            return createInhibitedItem(item, ['Cannot undeploy the TraceID dimension.']);
          } else if (isHasRelated(item)) {
            return createInhibitedItem(item, ['Cannot undeploy events used in metrics or flows.']);
          } else {
            return createInhibitedItem(item, ['Already in undeployed status.']);
          }
        },
      ),

      disabledDeletedActionData: filterAndMap(
        (item) => isTraceId(item) || isHasRelated(item),
        (item) => {
          if (isTraceId(item)) {
            return createInhibitedItem(item, ['Cannot delete the TraceID dimension.']);
          } else if (isHasRelated(item)) {
            return createInhibitedItem(item, ['Cannot delete events used in metrics or flows.']);
          } else {
            return item;
          }
        },
      ),
    };
  }, [propsData]);

  const {
    // Enabled
    enabledDeployedActionData,
    enabledUndeployedActionData,
    enabledDeletedActionData,
    // Disabled
    disabledDeployedActionData,
    disabledUndeployActionData,
    disabledDeletedActionData,
  } = categorizedPropsData;

  const rowDataByIdMap = useMemo(
    () => new Map(propsData.map((item) => [item[idKey], item])),
    [idKey, propsData],
  );

  // table data
  const canBulkActionTableData = useMemo(() => {
    let tableData: any[] = [];
    if (isDeployedAction) tableData = undeployedStatusTableData;
    if (isUndeployedAction) tableData = enabledUndeployedActionData;
    if (isDeletedAction) tableData = enabledDeletedActionData;
    return tableData;
  }, [
    enabledDeletedActionData,
    undeployedStatusTableData,
    enabledUndeployedActionData,
    isDeletedAction,
    isDeployedAction,
    isUndeployedAction,
  ]);

  const notBulkActionTableData = useMemo(() => {
    if (isDeployedAction) return disabledDeployedActionData;
    if (isUndeployedAction) return disabledUndeployActionData;
    return disabledDeletedActionData;
  }, [
    disabledDeletedActionData,
    disabledDeployedActionData,
    disabledUndeployActionData,
    isDeployedAction,
    isUndeployedAction,
  ]);

  // bulk action count
  const totalCount = useMemo(() => propsData.length, [propsData]);
  const canBulkActionCount = useMemo(() => canBulkActionTableData.length, [canBulkActionTableData]);
  const notBulkActionCount = useMemo(() => notBulkActionTableData.length, [notBulkActionTableData]);

  // bulk action title
  const canBulkActionTitle = useMemo(() => {
    return `${canBulkActionCount} item${getPluralSuffix(
      canBulkActionCount,
    )} will be ${bulkActionType} ${isDeletedAction ? 'to Pulse' : ''}`;
  }, [isDeletedAction, bulkActionType, canBulkActionCount]);

  const notBulkActionTitle = useMemo(() => {
    return `${notBulkActionCount} item${getPluralSuffix(
      notBulkActionCount,
    )} can’t be ${bulkActionType}`;
  }, [bulkActionType, notBulkActionCount]);

  // A collection of ids for data
  const bulkActionIds = useMemo(
    () => getIdsByResource({ resource, data: canBulkActionTableData, actionType: bulkActionType }),
    [resource, canBulkActionTableData, bulkActionType],
  );
  const undeployedIds = useMemo(
    () =>
      getIdsByResource({ resource, data: enabledDeployedActionData, actionType: bulkActionType }),
    [resource, enabledDeployedActionData, bulkActionType],
  );

  // column name key
  const nameDataKey = useMemo(() => getNameKeyByResource(resource), [resource]);

  // fetch undeployed diff data
  useEffect(() => {
    if (undeployedIds.length > 0) {
      getDeployListByIds(
        { resource, payload: { ids: undeployedIds } },
        {
          onSuccess: (data) => {
            // Add 'status' field
            // The purpose is to display a status column in the diff table
            const dataWithStatusAndDraft =
              data?.data?.map((item) => {
                const itemWithStatus = {
                  ...item,
                  status: EStatus.UNDEPLOYED,
                };
                // Attempt to find corresponding row data from the map
                const managementRowData = rowDataByIdMap.get(item.id);

                // To display the Draft tag.
                if (managementRowData) {
                  const { is_multi_version, is_draft } = managementRowData;
                  return {
                    ...itemWithStatus,
                    is_multi_version,
                    is_draft,
                  };
                }

                // Default case: add a 'status' field with a default value
                return itemWithStatus;
              }) ?? [];

            setUndeployedStatusTableData(dataWithStatusAndDraft);
          },
          onError: (error: any) => {
            message.error(error?.response?.data?.msg);
          },
        },
      );
    } else {
      setUndeployedStatusTableData([]);
    }
  }, [getDeployListByIds, undeployedIds, resource, rowDataByIdMap]);

  const resetRequestState = () => {
    setRequestStatus(null);
    setRequestData([]);
  };

  const handleTryAgain = () => {
    resetRequestState();
    refetchFlowRelated();
    refetchMetricRelated();
    refetchDimensionRelated();
    refetchMappedEventRelated();
  };

  const onOpenDrawer = (type?) => {
    setDrawerOpen(true);
    if (type) {
      setBulkActionType(type);
    }
  };

  const onCloseDrawer = () => {
    setDrawerOpen(false);
    resetRequestState();
    if (requestStatus === 'success') {
      onClose?.();
    }
  };

  const handleDeploy = () => {
    setRequestData(enabledDeployedActionData);
    deploy(
      {
        resource: resource,
        payload: {
          deploy_all: false,
          ids: bulkActionIds,
        },
      },
      {
        onSuccess: () => {
          message.success('Deploy Successfully!');
          setRequestStatus('success');
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
          setRequestStatus('failure');
        },
      },
    );
  };

  const handleUndeploy = () => {
    setRequestData(enabledUndeployedActionData);
    undeploy(
      {
        resource: resource,
        payload: {
          ids: bulkActionIds,
        },
      },
      {
        onSuccess: () => {
          message.success('Undeploy Successfully!');
          setRequestStatus('success');
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
          setRequestStatus('failure');
        },
      },
    );
  };

  const handleDelete = () => {
    setRequestData(enabledDeletedActionData);
    deleteVersions(
      { type: resource, ids: bulkActionIds },
      {
        onSuccess: () => {
          message.success('Delete Successfully!');
          setRequestStatus('success');
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
          setRequestStatus('failure');
        },
      },
    );
  };

  const generateSortableColumnProps = (title: string, dataKey: string) => ({
    column: {
      title,
      dataKey,
    },
    dataIndex: dataKey,
    key: dataKey,
  });

  const statusColumn = generateStatusColumn();
  const latestUpdatedByColumn = generateUserColumn(
    generateSortableColumnProps('Latest updated by', 'updated_by'),
  );
  const latestUpdatedAtColumn = generateDateColumn(
    generateSortableColumnProps('Latest updated at', 'updated_at'),
  );
  const editedByColumn = generateUserColumn(
    generateSortableColumnProps('Latest updated by', 'last_updated_by'),
  );
  const editedAtColumn = generateDateColumn(
    generateSortableColumnProps('Latest updated at', 'edited_time'),
  );
  const typeColumn = generateTypeColumn({
    column: {
      headerStyle: { padding: 0 },
    },
  });

  const columns = [
    generateNameColumn({ dataIndex: nameDataKey, key: nameDataKey }),
    statusColumn,
    latestUpdatedByColumn,
    latestUpdatedAtColumn,
  ];

  const disabledActionTableColumns = [
    generateNameColumn({ dataIndex: nameDataKey, key: nameDataKey }),
    statusColumn,
    generateCommonColumn(),
  ];

  const diffTableColumns = [
    Table.EXPAND_COLUMN,
    generateNameColumn(),
    statusColumn,
    typeColumn,
    editedByColumn,
    editedAtColumn,
  ];

  const disabledButton = canBulkActionTableData.length < 1;

  const getFooterButtonText = () => {
    if (isDeployedAction) return 'Deploy';
    if (isUndeployedAction) return 'Undeploy';
    if (isDeletedAction) return 'Delete';
    return '';
  };

  const modalFooter = (
    <Footer>
      <Button
        type="primary"
        onClick={() => {
          if (isDeployedAction) handleDeploy();
          if (isUndeployedAction) handleUndeploy();
          if (isDeletedAction) handleDelete();
          trackClickBulkActionManagement({
            resource,
            actionType: getFooterButtonText(),
            data: canBulkActionTableData,
            disabledActionData: notBulkActionTableData,
          });
        }}
        loading={loading}
        disabled={disabledButton}
        danger={!isDeployedAction}
        style={
          disabledButton
            ? {
                color: '#fff',
                backgroundColor: isDeployedAction ? '#0046ff' : '#F5222D',
                borderColor: 'transparent',
                opacity: 0.4,
              }
            : undefined
        }
      >
        {getFooterButtonText()}
      </Button>
      <Button onClick={onCloseDrawer}>Cancel</Button>
    </Footer>
  );

  return (
    <>
      {show && (
        <BulkActionWrapper>
          <span>
            Bulk action for {totalCount} selected item{getPluralSuffix(totalCount)}
          </span>
          {!workbenchMode && (
            <>
              <Button type="primary" ghost onClick={() => onOpenDrawer(DEPLOYED)}>
                Deploy
              </Button>
              <Button danger ghost onClick={() => onOpenDrawer(UNDEPLOYED)}>
                Undeploy
              </Button>
            </>
          )}
          <Button danger ghost onClick={() => onOpenDrawer(DELETED)}>
            Delete
          </Button>
        </BulkActionWrapper>
      )}
      <StyledDrawer
        open={isDrawerOpen}
        onClose={onCloseDrawer}
        width={1000}
        destroyOnClose
        title={
          <StyledHeader>
            {isDeployedAction ? <span>Deployment Confirmation</span> : null}
            {isUndeployedAction ? <span>Undeploy Confirmation</span> : null}
            {isDeletedAction ? <span>Delete Confirmation</span> : null}
          </StyledHeader>
        }
        footer={requestStatus ? null : modalFooter}
      >
        {requestStatus ? (
          <RequestCompletionPrompt
            requestStatus={requestStatus}
            resource={resource}
            actionType={bulkActionType}
            data={requestData}
            onCancel={onCloseDrawer}
            onTryAgain={handleTryAgain}
          />
        ) : (
          <Spin spinning={loading}>
            {canBulkActionTableData.length ? (
              <>
                <TableTitleWrapper
                  style={isDeletedAction ? { color: '#F5222D' } : { color: '#52C41A' }}
                >
                  <CheckOutlined />
                  <span>{canBulkActionTitle}</span>
                </TableTitleWrapper>
                <DeploymentTable
                  tableProps={
                    isDeployedAction
                      ? {
                          dataSource: canBulkActionTableData,
                          columns: diffTableColumns,
                        }
                      : {
                          dataSource: canBulkActionTableData,
                          columns,
                          expandable: undefined,
                        }
                  }
                />
              </>
            ) : null}

            {notBulkActionTableData.length ? (
              <>
                <TableTitleWrapper style={disabledButton ? undefined : { marginTop: '48px' }}>
                  <MinusCircleOutlined />
                  <span>{notBulkActionTitle}</span>
                </TableTitleWrapper>
                <DeploymentTable
                  tableProps={{
                    dataSource: notBulkActionTableData,
                    columns: disabledActionTableColumns,
                    expandable: undefined,
                  }}
                />
              </>
            ) : null}
          </Spin>
        )}
      </StyledDrawer>
    </>
  );
};

export default BulkAction;
